import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context';
import api from '../api/client';
import { formatPrice } from './ListingsPage';

// Bendri stiliai
const cardStyle = { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px' };
const inputStyle = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 };

function PageHeader({ icon, title, subtitle, action }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ marginBottom: 28 }}>
      <button className="btn btn-secondary btn-sm" style={{ marginBottom: 16 }} onClick={() => navigate('/events')}>← {t('events.title')}</button>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 32 }}>{icon}</span>
            <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 28, color: '#f0c040', margin: 0, fontWeight: 700 }}>{title}</h1>
          </div>
          <p style={{ color: 'rgba(200,180,255,0.6)', fontSize: 14, margin: 0, maxWidth: 640 }}>{subtitle}</p>
        </div>
        {action}
      </div>
    </div>
  );
}

// ══ BENDRAS SIDEBAR: skelbimai + žaidėjai + renginiai ═══════════════════════

function CommunitySidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [players, setPlayers] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get('/listings?page=1&limit=50').then(r => {
      const shuffled = [...(r.data.listings || [])].sort(() => Math.random() - 0.5);
      setListings(shuffled.slice(0, 5));
    }).catch(() => {});

    api.get('/players/random?count=5').then(r => setPlayers(r.data || [])).catch(() => {});

    api.get('/events').then(r => {
      const today = new Date().toISOString().split('T')[0];
      const upcoming = (r.data || [])
        .filter(e => e.date >= today)
        .sort((a, b) => (a.date < b.date ? -1 : 1));
      setEvents(upcoming.slice(0, 5));
    }).catch(() => {});
  }, []);

  return (
    <div className="pl-sidebar">
      {listings.length > 0 && (
        <div className="pl-sidebar-card">
          <div className="pl-sidebar-title">🛒 {t('community.sb_listings')}</div>
          {listings.map(l => (
            <div key={l.id} className="pl-sidebar-item" onClick={() => navigate(`/skelbimai/${l.id}`)}>
              {l.images?.[0] ? (
                <img src={l.images[0]} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <span className="pl-sidebar-emoji">🎲</span>
              )}
              <div style={{ minWidth: 0 }}>
                <div className="pl-sidebar-name">{l.title}</div>
                <div className="pl-sidebar-sub">
                  {l.listing_type === 'exchange' && !Number(l.price) ? `🔄 ${t('listings.type_exchange')}` : formatPrice(l.price, l.currency)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {players.length > 0 && (
        <div className="pl-sidebar-card">
          <div className="pl-sidebar-title">🎲 {t('community.sb_players')}</div>
          {players.map(p => (
            <div key={p.user_id} className="pl-sidebar-item" onClick={() => navigate(`/players/${p.user_id}`)}>
              {p.avatar ? (
                <img src={p.avatar} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <span className="pl-sidebar-emoji">🧙</span>
              )}
              <div style={{ minWidth: 0 }}>
                <div className="pl-sidebar-name">{p.name}</div>
                {p.city && <div className="pl-sidebar-sub">📍 {p.city}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {events.length > 0 && (
        <div className="pl-sidebar-card">
          <div className="pl-sidebar-title">⚔️ {t('community.sb_events')}</div>
          {events.map(e => (
            <div key={e.id} className="pl-sidebar-item" onClick={() => navigate(`/events/${e.id}`)}>
              <span className="pl-sidebar-emoji">{e.type === 'larp' ? '🗡️' : '🎲'}</span>
              <div style={{ minWidth: 0 }}>
                <div className="pl-sidebar-name">{e.name}</div>
                <div className="pl-sidebar-sub">{e.date}{e.city ? ` · ${e.city}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══ KONKURSAI IR IŠŠŪKIAI ════════════════════════════════════════════════════

const CONTEST_CATEGORIES = ['Fantasy kūryba', 'Cosplay', 'Piešimas', 'LARP herojaus kūrimas', 'DM rinkimai', 'Kita'];

const EMPTY_CONTEST = { title: '', category: CONTEST_CATEGORIES[0], description: '', prize: '', deadline: '', status: 'active' };

function ContestForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_CONTEST);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { setError('Pavadinimas privalomas'); return; }
    setSaving(true); setError('');
    try {
      const r = initial?.id ? await api.put(`/contests/${initial.id}`, form) : await api.post('/contests', form);
      onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Nepavyko išsaugoti');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ ...cardStyle, border: '1px solid rgba(240,192,64,0.25)', marginBottom: 24 }}>
      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: 'var(--gold2)', marginBottom: 16 }}>
        {initial?.id ? '✏️ Redaguoti konkursą' : '🏆 Naujas konkursas'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div><label style={labelStyle}>Pavadinimas *</label>
          <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} maxLength={200} /></div>
        <div><label style={labelStyle}>Kategorija</label>
          <select style={inputStyle} value={form.category} onChange={e => set('category', e.target.value)}>
            {CONTEST_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><label style={labelStyle}>Terminas</label>
          <input style={inputStyle} type="date" value={form.deadline} onChange={e => set('deadline', e.target.value)} /></div>
      </div>
      <div style={{ marginBottom: 14 }}><label style={labelStyle}>Aprašymas</label>
        <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={4} value={form.description} onChange={e => set('description', e.target.value)} maxLength={4000} /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 14 }}>
        <div><label style={labelStyle}>Prizas / titulas</label>
          <input style={inputStyle} value={form.prize} onChange={e => set('prize', e.target.value)} maxLength={300} placeholder="pvz. Titulas + 50 EUR kuponas" /></div>
        <div><label style={labelStyle}>Statusas</label>
          <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="active">Aktyvus</option>
            <option value="finished">Pasibaigęs</option>
            <option value="draft">Juodraštis</option>
          </select></div>
      </div>
      {error && <div style={{ color: '#ff6060', fontSize: 13, marginBottom: 10 }}>⚠️ {error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? '⏳' : '💾 Išsaugoti'}</button>
        <button className="btn btn-secondary" onClick={onCancel}>Atšaukti</button>
      </div>
    </div>
  );
}

export function ContestsPage() {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get('/contests').then(r => setContests(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Ištrinti konkursą?')) return;
    await api.delete(`/contests/${id}`);
    setContests(cs => cs.filter(c => c.id !== id));
  };

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 20px' }}>
      <PageHeader
        icon="🏆" title={t('events_menu.contests_title')}
        subtitle={t('events_menu.contests_desc')}
        action={isAdmin && !showForm && <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>➕ Naujas konkursas</button>}
      />

      <div className="community-layout">
      <div>
      {showForm && <ContestForm initial={editing} onSaved={() => { setShowForm(false); setEditing(null); load(); }} onCancel={() => { setShowForm(false); setEditing(null); }} />}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>⏳ {t('common.loading')}</div>
      ) : contests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🏆</div>{t('community.no_contests')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {contests.map(c => (
            <div key={c.id} style={{ ...cardStyle, opacity: c.status === 'finished' ? 0.65 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 6, background: 'rgba(240,192,64,0.12)', border: '1px solid rgba(240,192,64,0.35)', color: '#f0c040' }}>{c.category || 'Konkursas'}</span>
                {c.status === 'finished' && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 6, background: 'rgba(136,136,136,0.12)', color: '#888', textTransform: 'uppercase' }}>{t('community.finished')}</span>}
              </div>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{c.title}</div>
              <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, margin: '0 0 12px' }}>{c.description}</p>
              {c.prize && <div style={{ fontSize: 12.5, color: '#f0c040', marginBottom: 6 }}>🏅 <b>{t('community.prize')}:</b> {c.prize}</div>}
              {c.deadline && <div style={{ fontSize: 12, color: 'var(--text3)' }}>⏳ {t('community.until')}: {c.deadline}</div>}
              {isAdmin && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(c); setShowForm(true); window.scrollTo(0, 0); }}>✏️</button>
                  <button className="btn btn-secondary btn-sm" style={{ color: '#ff8080' }} onClick={() => remove(c.id)}>🗑️</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!user && (
        <div style={{ marginTop: 32, textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>
          🔐 {t('community.login_contests')}
        </div>
      )}
      </div>
      <CommunitySidebar />
      </div>
    </div>
  );
}

// ══ BALSAVIMAI IR RINKIMAI ═══════════════════════════════════════════════════

function NewPollForm({ onSaved, onCancel }) {
  const [form, setForm] = useState({ title: '', description: '', ends_at: '', options: ['', ''] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setOpt = (i, v) => setForm(f => ({ ...f, options: f.options.map((o, x) => x === i ? v : o) }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await api.post('/polls', { ...form, options: form.options.filter(o => o.trim()) });
      onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Nepavyko sukurti');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ ...cardStyle, border: '1px solid rgba(128,176,255,0.3)', marginBottom: 24 }}>
      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: '#80b0ff', marginBottom: 16 }}>🗳️ Naujas balsavimas</div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 14 }}>
        <div><label style={labelStyle}>Klausimas *</label>
          <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} maxLength={200} placeholder="pvz. Geriausias metų klubas?" /></div>
        <div><label style={labelStyle}>Baigiasi</label>
          <input style={inputStyle} type="date" value={form.ends_at} onChange={e => set('ends_at', e.target.value)} /></div>
      </div>
      <div style={{ marginBottom: 14 }}><label style={labelStyle}>Aprašymas</label>
        <input style={inputStyle} value={form.description} onChange={e => set('description', e.target.value)} maxLength={2000} /></div>
      <label style={labelStyle}>Pasirinkimai (min. 2)</label>
      {form.options.map((o, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input style={inputStyle} value={o} onChange={e => setOpt(i, e.target.value)} placeholder={`Pasirinkimas ${i + 1}`} maxLength={200} />
          {form.options.length > 2 && (
            <button className="btn btn-secondary btn-sm" onClick={() => set('options', form.options.filter((_, x) => x !== i))}>✕</button>
          )}
        </div>
      ))}
      {form.options.length < 12 && (
        <button className="btn btn-secondary btn-sm" style={{ marginBottom: 14 }} onClick={() => set('options', [...form.options, ''])}>➕ Pridėti pasirinkimą</button>
      )}
      {error && <div style={{ color: '#ff6060', fontSize: 13, marginBottom: 10 }}>⚠️ {error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? '⏳' : '📤 Sukurti'}</button>
        <button className="btn btn-secondary" onClick={onCancel}>Atšaukti</button>
      </div>
    </div>
  );
}

function PollCard({ poll, user, isAdmin, onChanged, onDelete }) {
  const { t } = useTranslation();
  const [voting, setVoting] = useState(false);
  const closed = poll.status !== 'active';

  const vote = async (optionId) => {
    if (!user || voting || closed) return;
    setVoting(true);
    try {
      const r = await api.post(`/polls/${poll.id}/vote`, { option_id: optionId });
      onChanged(r.data);
    } catch { /* ignore */ } finally { setVoting(false); }
  };

  const toggleStatus = async () => {
    const status = closed ? 'active' : 'closed';
    await api.patch(`/polls/${poll.id}/status?status=${status}`);
    onChanged({ ...poll, status });
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
        <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{poll.title}</div>
        {closed && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 6, background: 'rgba(136,136,136,0.12)', color: '#888', flexShrink: 0, textTransform: 'uppercase' }}>{t('community.closed')}</span>}
      </div>
      {poll.description && <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 12px' }}>{poll.description}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {poll.options.map(o => {
          const pct = poll.total_votes ? Math.round((o.votes / poll.total_votes) * 100) : 0;
          const mine = poll.my_vote === o.id;
          return (
            <div
              key={o.id}
              onClick={() => vote(o.id)}
              style={{ position: 'relative', border: `1px solid ${mine ? 'rgba(128,176,255,0.7)' : 'var(--border2)'}`, borderRadius: 8, padding: '8px 12px', cursor: user && !closed ? 'pointer' : 'default', overflow: 'hidden' }}
            >
              <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: mine ? 'rgba(128,176,255,0.18)' : 'rgba(128,176,255,0.08)', transition: 'width .3s' }} />
              <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--text)', fontWeight: mine ? 700 : 400 }}>{mine ? '✓ ' : ''}{o.label}</span>
                <span style={{ color: 'var(--text3)', flexShrink: 0 }}>{o.votes} ({pct}%)</span>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, fontSize: 12, color: 'var(--text3)' }}>
        <span>👥 {poll.total_votes} {t('community.votes')}</span>
        {poll.ends_at && <span>⏳ {t('community.until')} {poll.ends_at}</span>}
        {!user && <span>🔐 {t('community.login_vote')}</span>}
        {isAdmin && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={toggleStatus}>{closed ? '▶ Atidaryti' : '⏸ Uždaryti'}</button>
            <button className="btn btn-secondary btn-sm" style={{ color: '#ff8080' }} onClick={() => onDelete(poll.id)}>🗑️</button>
          </span>
        )}
      </div>
    </div>
  );
}

export function PollsPage() {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    api.get('/polls').then(r => setPolls(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onChanged = (p) => setPolls(ps => ps.map(x => x.id === p.id ? { ...x, ...p } : x));
  const onDelete = async (id) => {
    if (!window.confirm('Ištrinti balsavimą?')) return;
    await api.delete(`/polls/${id}`);
    setPolls(ps => ps.filter(p => p.id !== id));
  };

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 20px' }}>
      <PageHeader
        icon="🗳️" title={t('events_menu.polls_title')}
        subtitle={t('events_menu.polls_desc')}
        action={isAdmin && !showForm && <button className="btn btn-primary" onClick={() => setShowForm(true)}>➕ Naujas balsavimas</button>}
      />

      <div className="community-layout">
      <div>
      {showForm && <NewPollForm onSaved={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>⏳ {t('common.loading')}</div>
      ) : polls.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🗳️</div>{t('community.no_polls')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {polls.map(p => (
            <PollCard key={p.id} poll={p} user={user} isAdmin={isAdmin} onChanged={onChanged} onDelete={onDelete} />
          ))}
        </div>
      )}
      </div>
      <CommunitySidebar />
      </div>
    </div>
  );
}

// ══ KELIONIŲ IR RENGINIŲ PLANUOKLĖ ═══════════════════════════════════════════

const TRAVEL_TYPE_META = {
  plan:       { labelKey: 'community.type_plan',       icon: '🗺️', color: '#80d060' },
  experience: { labelKey: 'community.type_experience', icon: '📖', color: '#c8a0ff' },
};

const EMPTY_TRAVEL = { type: 'plan', title: '', event_name: '', country: '', city: '', date_from: '', date_to: '', transport: '', lodging: '', content: '' };

function TravelForm({ initial, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial || EMPTY_TRAVEL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isPlan = form.type === 'plan';

  const save = async () => {
    if (!form.title.trim()) { setError('Pavadinimas privalomas'); return; }
    setSaving(true); setError('');
    try {
      const r = initial?.id ? await api.put(`/travel/${initial.id}`, form) : await api.post('/travel', form);
      onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Nepavyko išsaugoti');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ ...cardStyle, border: '1px solid rgba(128,208,96,0.3)', marginBottom: 24 }}>
      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: '#80d060', marginBottom: 16 }}>
        {initial?.id ? '✏️ Redaguoti įrašą' : '🗺️ Naujas įrašas'}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {Object.entries(TRAVEL_TYPE_META).map(([k, m]) => (
          <button key={k} type="button" onClick={() => set('type', k)}
            style={{ background: form.type === k ? m.color + '22' : 'var(--bg)', border: `2px solid ${form.type === k ? m.color : 'var(--border2)'}`, color: form.type === k ? m.color : 'var(--text2)', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {m.icon} {t(m.labelKey)}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 14 }}>
        <div><label style={labelStyle}>Pavadinimas *</label>
          <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} maxLength={200} placeholder={isPlan ? 'pvz. Vykstam į Comic Con Baltics!' : 'pvz. Mano patirtis Dungeon Con 2025'} /></div>
        <div><label style={labelStyle}>Renginys</label>
          <input style={inputStyle} value={form.event_name} onChange={e => set('event_name', e.target.value)} maxLength={200} placeholder="Comic Con, Dungeon Con..." /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div><label style={labelStyle}>Šalis</label>
          <input style={inputStyle} value={form.country} onChange={e => set('country', e.target.value)} maxLength={100} /></div>
        <div><label style={labelStyle}>Miestas</label>
          <input style={inputStyle} value={form.city} onChange={e => set('city', e.target.value)} maxLength={100} /></div>
        <div><label style={labelStyle}>Nuo</label>
          <input style={inputStyle} type="date" value={form.date_from} onChange={e => set('date_from', e.target.value)} /></div>
        <div><label style={labelStyle}>Iki</label>
          <input style={inputStyle} type="date" value={form.date_to} onChange={e => set('date_to', e.target.value)} /></div>
      </div>
      {isPlan && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div><label style={labelStyle}>Transportas</label>
            <input style={inputStyle} value={form.transport} onChange={e => set('transport', e.target.value)} maxLength={300} placeholder="pvz. Turiu 3 laisvas vietas aute / Ieškau transporto" /></div>
          <div><label style={labelStyle}>Nakvynė</label>
            <input style={inputStyle} value={form.lodging} onChange={e => set('lodging', e.target.value)} maxLength={300} placeholder="pvz. Ieškau su kuo dalintis Airbnb" /></div>
        </div>
      )}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{isPlan ? 'Aprašymas' : 'Jūsų patirtis *'}</label>
        <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={isPlan ? 4 : 7} value={form.content} onChange={e => set('content', e.target.value)} maxLength={8000}
          placeholder={isPlan ? 'Kelionės detalės, laikas, susitikimo vieta...' : 'Papasakokite apie savo kelionę į Comic Con, Dungeon Con ar kitą renginį: patarimai, įspūdžiai, ko vengti...'} />
      </div>
      {error && <div style={{ color: '#ff6060', fontSize: 13, marginBottom: 10 }}>⚠️ {error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? '⏳' : '📤 Skelbti'}</button>
        <button className="btn btn-secondary" onClick={onCancel}>Atšaukti</button>
      </div>
    </div>
  );
}

export function TravelPage() {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/travel${typeFilter ? `?type=${typeFilter}` : ''}`)
      .then(r => setPosts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [typeFilter]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Ištrinti įrašą?')) return;
    await api.delete(`/travel/${id}`);
    setPosts(ps => ps.filter(p => p.id !== id));
  };

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 20px' }}>
      <PageHeader
        icon="🗺️" title={t('events_menu.travel_title')}
        subtitle={t('events_menu.travel_desc_long')}
        action={user && !showForm && <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>➕ Naujas įrašas</button>}
      />

      <div className="community-layout">
      <div>
      {showForm && <TravelForm initial={editing} onSaved={() => { setShowForm(false); setEditing(null); load(); }} onCancel={() => { setShowForm(false); setEditing(null); }} />}

      {/* Filtras */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setTypeFilter('')}
          style={{ background: !typeFilter ? 'rgba(240,192,64,0.18)' : 'var(--bg2)', border: `1px solid ${!typeFilter ? 'rgba(240,192,64,0.5)' : 'var(--border2)'}`, color: !typeFilter ? '#f0c040' : 'var(--text)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
          {t('common.all')}
        </button>
        {Object.entries(TRAVEL_TYPE_META).map(([k, m]) => (
          <button key={k} onClick={() => setTypeFilter(k)}
            style={{ background: typeFilter === k ? m.color + '22' : 'var(--bg2)', border: `1px solid ${typeFilter === k ? m.color : 'var(--border2)'}`, color: typeFilter === k ? m.color : 'var(--text)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
            {m.icon} {t(m.labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>⏳ {t('common.loading')}</div>
      ) : posts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🗺️</div>{t('community.no_posts')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {posts.map(p => {
            const m = TRAVEL_TYPE_META[p.type] || TRAVEL_TYPE_META.plan;
            const canEdit = user && (user.id === p.user_id || isAdmin);
            return (
              <div key={p.id} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 6, background: m.color + '22', border: `1px solid ${m.color}55`, color: m.color }}>{m.icon} {t(m.labelKey)}</span>
                  {p.event_name && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 6, background: 'rgba(240,192,64,0.1)', border: '1px solid rgba(240,192,64,0.3)', color: '#f0c040' }}>🎪 {p.event_name}</span>}
                </div>
                <div style={{ fontFamily: 'Cinzel,serif', fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{p.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                  {(p.country || p.city) && <span>📍 {[p.city, p.country].filter(Boolean).join(', ')}</span>}
                  {p.date_from && <span>📅 {p.date_from}{p.date_to && p.date_to !== p.date_from ? ` – ${p.date_to}` : ''}</span>}
                </div>
                {p.transport && <div style={{ fontSize: 12.5, color: '#80d060', marginBottom: 4 }}>🚗 {p.transport}</div>}
                {p.lodging && <div style={{ fontSize: 12.5, color: '#80b0ff', marginBottom: 8 }}>🏨 {p.lodging}</div>}
                {p.content && <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, margin: '0 0 10px', whiteSpace: 'pre-wrap' }}>{p.content}</p>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text3)', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  <Link to={`/players/${p.user_id}`} style={{ color: 'var(--gold2)', textDecoration: 'none', fontWeight: 600 }}>👤 {p.author_name}</Link>
                  <span>🕒 {(p.created_at || '').slice(0, 10)}</span>
                  {canEdit && (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(p); setShowForm(true); window.scrollTo(0, 0); }}>✏️</button>
                      <button className="btn btn-secondary btn-sm" style={{ color: '#ff8080' }} onClick={() => remove(p.id)}>🗑️</button>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!user && (
        <div style={{ marginTop: 32, background: 'rgba(90,62,140,0.1)', border: '1px dashed rgba(90,62,140,0.4)', borderRadius: 12, padding: '20px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔐</div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>{t('community.login_travel')}</div>
        </div>
      )}
      </div>
      <CommunitySidebar />
      </div>
    </div>
  );
}
